import singlePageProps from '../../lib/singlePageProps';
import Image from 'next/legacy/image';
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Text
} from '@chakra-ui/react';
import ArrowRight from 'remixicon-react/ArrowRightSLineIcon';
import {
  CShapeBlurPattern,
  GridPatternDark,
  LayoutContainer,
  RoundedCorner
} from '@components';

type IconName = 'email' | 'discord' | 'twitter';

type Props = {
  text: {
    heroTitle: string;
    heroDesc: string;
  };
  json: {
    supportLinksData: Array<{
      id: number;
      name: string;
      description: string;
      href: string;
      icon: IconName;
      cta: string;
    }>;
  };
};

function getIcon(iconName: IconName): string {
  switch (iconName) {
    case 'email':
      return '/images/support/icon-email.svg';
    case 'twitter':
      return '/images/support/icon-twitter.svg';
    case 'discord':
      return '/images/support/icon-discord.svg';
  }
}

export default function SupportPage({ text, json }: Props) {
  return (
    <LayoutContainer pos="relative" verticalPadding="md">
      <CShapeBlurPattern rotate={30} top="0px" left="200px" size="800px" />
      <CShapeBlurPattern rotate={180} top="50px" right="-100px" size="1000px" />
      <AspectRatio ratio={1400 / 1190} w="1000px" pos="absolute" zIndex={0}>
        <GridPatternDark />
      </AspectRatio>

      <Flex zIndex={10} direction="column" align="center" pos="relative">
        <Heading as="h1" size="h1" mb={4}>
          {text.heroTitle}
        </Heading>

        <Text textStyle="paragraph-md" maxW="576px" textAlign="center" mb={12}>
          {text.heroDesc}
        </Text>

        <Flex gap={{ base: 8, lg: 12 }} wrap="wrap">
          {json.supportLinksData.map(
            ({ id, name, description, icon, cta, href }) => (
              <Flex
                direction="column"
                key={id}
                p={6}
                bg="white"
                rounded="xl"
                justify="space-between"
                minW="270px"
                flex={1}
                shadow="dropShadow1"
              >
                <Flex direction="column">
                  <Box mb={6}>
                    <Image src={getIcon(icon)} alt="" height="40" width="40" />
                  </Box>

                  <Heading size="h5" mb={3}>
                    {name}
                  </Heading>

                  <Text
                    textStyle="paragraph-md"
                    mb={6}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </Flex>

                <Button
                  as="a"
                  href={href}
                  target="_blank"
                  rel="noopener"
                  variant="outline"
                  borderColor="gray.100"
                  _hover={{
                    borderColor: 'primary.500'
                  }}
                  _active={{
                    bg: 'gray.50'
                  }}
                >
                  {cta}

                  <Icon as={ArrowRight} ml={3} />
                </Button>
              </Flex>
            )
          )}
        </Flex>
      </Flex>

      <Box display={{ base: 'none', lg: 'inline-flex' }}>
        <RoundedCorner
          canvasHeight={160}
          bg={'#F8F9FC'}
          yPosition="bottom"
          xPosition="left"
        />
        <RoundedCorner
          canvasHeight={160}
          bg={'#F8F9FC'}
          yPosition="bottom"
          xPosition="right"
        />
      </Box>
    </LayoutContainer>
  );
}

const slug = '/support';
export async function getStaticProps({ preview = false }) {
  return singlePageProps({ preview, slug });
}
